import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import accountApi from '../../account/api/accountApi';
import AccountRoles from '../../account/enums/AccountRoles';
import ConfirmationDialog from '../../common/component/form/ConfirmationDialog';
import {sortCaseInsensitive} from '../../common/util/SortUtil';
import {noEntryOptional} from '../../training/util/TrainingUtilities';
import AccountDialog from './AccountDialog';
import Table from '../../common/component/table/Table';
import ReactTooltip from 'react-tooltip';
import '../style/ClubOverview.less';

function UserList({
                      club
                  }) {

    const role = useSelector(state => state.account.role);
    const accountId = useSelector(state => state.account.id);
    const [t] = useTranslation();

    const [accounts, setAccounts] = useState([]);

    const [selectedAccountId, setSelectedAccountId] = useState(undefined);
    const [showDeletionConfirmation, setShowDeletionConfirmation] = useState(false);
    const [showAccountActivationConfirmation, setShowAccountActivationConfirmation] = useState(false);
    const [showAccountRejectionConfirmation, setShowAccountRejectionConfirmation] = useState(false);
    const [resendDOIConfirmation, setResendDOIConfirmation] = useState(false);
    const [selectedAccount, setSelectedAccount] = useState(null);
    const [showEditDialog, setShowEditDialog] = useState(false);
    const [canAddAccount, setCanAddAccount] = useState(true);
    const [selectableRoles, setSelectableRoles] = useState([]);
    const [dialogTitle, setDialogTitle] = useState(t('Account.ADD'));

    useEffect(() => {
        loadAccounts();
        columns['lastName']?.sort((a, b) => sortCaseInsensitive(a, b));
    }, [club]);

    useEffect(() => {
        ReactTooltip.rebuild();
    });

    const loadAccounts = () => {
        accountApi.findByClubId(club?.id).then((response) => {
            if (response.data.success) {
                setAccounts(response.data.result);
            }
        });
        getRolesToAdd();
    };

    const deleteAccount = () => {
        accountApi.delete(selectedAccountId).then((response) => {
            if (response.data.success) {
                loadAccounts();
                setSelectedAccountId(null);
            }
        });
    };

    const verifyAccount = () => {
        accountApi.verify(selectedAccountId).then((response) => {
            if (response.data.success) {
                loadAccounts();
                setSelectedAccountId(null);
            }
        });
    };

    const rejectAccount = () => {
        accountApi.rejectAccountVerification(selectedAccountId).then((response) => {
            if (response.data.success) {
                loadAccounts();
                setSelectedAccountId(null);
            }
        });
    };

    const resendConfirmationMail = () => {
        accountApi.resendConfirmationMail(selectedAccount.id).then((response) => {
            if (response.data.success) {
                setSelectedAccount(null);
                setResendDOIConfirmation(false);
            }
        });
    };

    const getRolesToAdd = () => {
        accountApi.getRolesToAdd(club?.id).then((response) => {
            let allowAccountAddition = false;
            let newSelectableRoles = [];

            if (response.data.success) {
                allowAccountAddition = response.data.result?.length > 0;
                newSelectableRoles = response.data.result;
            }
            setCanAddAccount(allowAccountAddition);
            setSelectableRoles(newSelectableRoles);
        });
    };

    const columns = [
        {
            Header: t('Account.DISPLAY_NAME'),
            accessor: 'lastName',
            Cell: props => {
                const text = `${props.cell.row.original.firstName ? props.cell.row.original.firstName : ''} ${props.cell.row.original.lastName}`;
                return noEntryOptional(text);
            },
            sortType: (a, b) => sortCaseInsensitive(a.original.lastName, b.original.lastName)
        }, {
            Header: t('Account.EMAIL'),
            accessor: 'email',
            Cell: props => noEntryOptional(props.cell.row.original.email),
            sortType: (a, b) => sortCaseInsensitive(a.original.email, b.original.email)
        }, {
            Header: t('Account.ROLE'),
            accessor: 'role',
            Cell: props => t(`Role.${props.cell.value}`),
            sortType: (a, b) => sortCaseInsensitive(a.original.role, b.original.role)
        }, {
            Header: t('Account.VERIFIED'),
            accessor: 'verified',
            Cell: props => {
                return (
                    <>
                        {
                            !props.cell.row.original.verified ?
                                <>
                                    <button
                                        className="btn btn-icon checkmark"
                                        onClick={() => {
                                            setSelectedAccountId(props.cell.row.original.id);
                                            setShowAccountActivationConfirmation(true);
                                        }}
                                        title={t('Button.EDIT')}
                                    >
                                        <FontAwesomeIcon icon="check"/>
                                    </button>
                                    <button
                                        className="btn x-delete btn-icon"
                                        onClick={() => {
                                            setSelectedAccountId(props.cell.row.original.id);
                                            setShowAccountRejectionConfirmation(true);
                                        }}
                                        title={t('Button.EDIT')}
                                    >
                                        <FontAwesomeIcon icon="times"/>
                                    </button>
                                </>
                                :
                                <>
                                    <div className="checkmark-light btn btn-transparent-secondary btn-icon ml-3">
                                        <FontAwesomeIcon icon="check"/>
                                    </div>
                                </>
                        }
                    </>
                );
            },
            sortType: (a, b) => sortCaseInsensitive(a.original.role, b.original.role)
        }, {
            Header: '',
            accessor: 'id',
            Cell: props => {
                const shouldRenderDeleteButton = props.cell.row.original.id !== accountId;
                return (
                    (role === AccountRoles.ADMINISTRATOR ||
                        role === AccountRoles.CLUB_MANAGER) &&
                    <>
                        {
                            shouldRenderDeleteButton ?
                                <button
                                    className="btn btn-transparent-secondary btn-icon"
                                    onClick={() => {
                                        setSelectedAccountId(props.cell.value);
                                        setShowDeletionConfirmation(true);
                                    }}
                                    title={t('Button.DELETE')}
                                >
                                    <FontAwesomeIcon icon="trash"/>
                                </button>
                                :
                                <span className="delete-button-spacer"/>
                        }
                        <button
                            className="btn btn-transparent-secondary btn-icon"
                            onClick={() => {
                                setSelectedAccount(accounts.find(account => account.id === props.cell.value));
                                setShowEditDialog(true);
                                setDialogTitle(t('Account.UPDATE'));
                            }}
                            title={t('Button.EDIT')}
                        >
                            <FontAwesomeIcon icon="pencil-alt"/>
                        </button>
                    </>
                );
            }
        }, {
            Header: '',
            accessor: 'status',
            Cell: props => {
                return (
                    <>
                        {
                            props.cell.row.original.verified && props.cell.row.original.status !== 'APPROVED' &&
                            <button
                                className="btn btn-primary"
                                onClick={() => {
                                    setSelectedAccount(props.cell.row.original);
                                    setResendDOIConfirmation(true);
                                }}
                                title={t('Button.SEND')}
                            >
                                {t('Account.RESEND_DOI')}
                            </button>
                        }
                    </>
                );
            }
        }
    ];

    let addUserTooltipText = null;
    if (!canAddAccount) {
        if (role === AccountRoles.ADMINISTRATOR) {
            addUserTooltipText = t('Account.FREE_USER_LIMIT_REACHED_ADMIN');
        } else {
            addUserTooltipText = t('Account.FREE_USER_LIMIT_REACHED');
        }
    }

    return (
        <>
            <ReactTooltip type="dark" multiline={true}/>
            <div className="align-right">
                <h3 className="list-heading">
                    {t('Account.TITLE')}
                </h3>
                {
                    (role === AccountRoles.ADMINISTRATOR || role === AccountRoles.CLUB_MANAGER) &&
                    <button
                        className={canAddAccount ? 'btn btn-primary ml-3' : 'btn btn-primary ml-3 disabled'}
                        data-tip={addUserTooltipText}
                        onClick={() => {
                            setShowEditDialog(true);
                            setDialogTitle(t('Account.ADD'));
                        }}
                        disabled={!canAddAccount}
                    >
                        <FontAwesomeIcon icon="plus" className="mr-2"/>
                        {t('Account.ADD')}
                    </button>
                }
            </div>

            <Table
                columns={columns}
                data={accounts}
                sortable={true}
                sortBy={'lastName'}
                initialSort={[{
                    id: 'lastName',
                    desc: false
                }]}
            />

            <ConfirmationDialog
                title={t('Dialog.REMOVE_USER')}
                open={showDeletionConfirmation}
                description={t('Dialog.REMOVE_USER_QUESTION')}
                accept={() => {
                    deleteAccount();
                    setSelectedAccountId(undefined);
                    setShowDeletionConfirmation(false);
                }}
                cancel={() => {
                    setSelectedAccountId(undefined);
                    setShowDeletionConfirmation(false);
                }}
                acceptLabel={t('Button.DELETE')}
            />
            <ConfirmationDialog
                title={t('Dialog.ACTIVATE_ACCOUNT')}
                open={showAccountActivationConfirmation}
                description={t('Dialog.ACTIVATE_ACCOUNT_QUESTION')}
                accept={() => {
                    verifyAccount();
                    setSelectedAccountId(undefined);
                    setShowAccountActivationConfirmation(false);
                }}
                cancel={() => {
                    setSelectedAccountId(undefined);
                    setShowAccountActivationConfirmation(false);
                }}
                acceptLabel={t('Button.VERIFY_ACCOUNT')}
            />
            <ConfirmationDialog
                title={t('Dialog.REJECT_ACTIVATION')}
                open={showAccountRejectionConfirmation}
                description={t('Dialog.REJECT_ACTIVATION_QUESTION')}
                accept={() => {
                    rejectAccount();
                    setSelectedAccountId(undefined);
                    setShowAccountRejectionConfirmation(false);
                }}
                cancel={() => {
                    setSelectedAccountId(undefined);
                    setShowAccountRejectionConfirmation(false);
                }}
                acceptLabel={t('Button.DELETE')}
            />
            <ConfirmationDialog
                title={t('Dialog.RESEND_DOI')}
                open={resendDOIConfirmation}
                description={t('Dialog.RESEND_DOI_QUESTION', {email: selectedAccount?.email})}
                accept={() => {
                    resendConfirmationMail();
                }}
                cancel={() => {
                    setSelectedAccount(null);
                    setResendDOIConfirmation(false);
                }}
                acceptLabel={t('Button.SEND')}
            />

            <AccountDialog
                clubId={club?.id}
                open={showEditDialog}
                title={dialogTitle}
                selectableRoles={selectableRoles}
                onSave={() => {
                    setShowEditDialog(false);
                    setSelectedAccount(null);
                    loadAccounts();
                }}
                onCancel={() => {
                    setShowEditDialog(false);
                    setSelectedAccount(null);
                }}
                account={selectedAccount}
            />
        </>
    );
}

export default UserList;
